import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { Decimal } from 'decimal.js';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { translating } from '../i18n/resolver.js';
import { useT } from '../i18n/useT.js';
import { formatDate, formatMoney, toIsoDate } from '../format.js';
import { useAuth } from '../auth/useAuth.js';
import {
  useCloseVatPeriod,
  useOpenVatPeriod,
  useVatPeriods,
  useVatReport,
} from '../api/hooks.js';
import type { VatPeriod, VatReport } from '../api/hooks.js';

/**
 * Moms — periods, the figures, and filing.
 *
 * Three things this page must not soften:
 *
 * 1. **An open period's figures are not a return.** They are summed from the
 *    journal on request and will move as postings are made. `isFinal` says so
 *    and the screen repeats it, because a number that looks like a filing and
 *    is not is the most dangerous thing here.
 * 2. **Closing is irreversible, in three ways at once** — the figures freeze,
 *    the period can never reopen, and nothing can ever be posted inside it
 *    again. The confirmation names all three rather than asking "are you sure?".
 * 3. **This does not fill in a momsangivelse.** It gives the figures; a person
 *    completes the return. Implying otherwise would invite a filing built on a
 *    mapping this system deliberately refuses to guess.
 */

export function VatPage(): ReactNode {
  const t = useT();
  const periods = useVatPeriods();
  const [selected, setSelected] = useState<string | null>(null);
  const [openOpened, openModal] = useDisclosure(false);

  const items = periods.data?.items ?? [];
  // The newest period is the one being worked on; falling back to it saves a
  // click on the only page where there is usually one answer.
  const activeId = selected ?? items[0]?.id ?? null;
  const active = items.find((period) => period.id === activeId) ?? null;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center" wrap="wrap">
        <Title order={2}>{t('vat.title')}</Title>
        <Button variant="light" onClick={openModal.open}>
          {t('vat.openPeriod')}
        </Button>
      </Group>

      <Alert color="gray" variant="light" title={t('vat.scopeTitle')}>
        {t('vat.scope')}
      </Alert>

      {periods.isPending && (
        <Center p="xl">
          <Loader />
        </Center>
      )}

      {periods.isError && <ErrorMessage error={periods.error} />}

      {periods.isSuccess && items.length === 0 && (
        <Text c="dimmed">{t('vat.none')}</Text>
      )}

      {items.length > 0 && (
        <>
          <Table.ScrollContainer minWidth={640}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('vat.col.period')}</Table.Th>
                  <Table.Th style={{ width: 120 }}>{t('vat.col.status')}</Table.Th>
                  <Table.Th style={{ width: 140 }} className="money-cell">
                    {t('vat.netVat')}
                  </Table.Th>
                  <Table.Th style={{ width: 110 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((period) => (
                  <Table.Tr key={period.id}>
                    <Table.Td>
                      <Text size="sm">
                        {formatDate(period.periodStart)}–{formatDate(period.periodEnd)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        variant="light"
                        color={period.status === 'closed' ? 'green' : 'yellow'}
                      >
                        {period.status === 'closed'
                          ? t('vat.status.closed')
                          : t('vat.status.open')}
                      </Badge>
                    </Table.Td>
                    <Table.Td className="money-cell">
                      {/*
                        Null while open, and shown as such. Rendering it as 0,00
                        would state that nothing is owed, which is a different
                        claim from "not yet determined".
                      */}
                      {period.netVat === null ? (
                        <Text size="sm" c="dimmed">
                          {t('vat.notDetermined')}
                        </Text>
                      ) : (
                        formatMoney(period.netVat)
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="compact-sm"
                        variant={period.id === activeId ? 'light' : 'subtle'}
                        onClick={() => {
                          setSelected(period.id);
                        }}
                      >
                        Vis
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          {active !== null && <VatReportPanel period={active} />}
        </>
      )}

      <OpenPeriodModal opened={openOpened} onClose={openModal.close} />
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// The figures
// -----------------------------------------------------------------------------

function VatReportPanel({ period }: { readonly period: VatPeriod }): ReactNode {
  const t = useT();
  const report = useVatReport(period.id);

  if (report.isPending) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }
  if (report.isError) {
    return <ErrorMessage error={report.error} title={t('vat.loadFailed')} />;
  }
  if (!report.isSuccess) {
    return null;
  }

  return <VatFigures report={report.data} period={period} />;
}

function VatFigures({
  report,
  period,
}: {
  readonly report: VatReport;
  readonly period: VatPeriod;
}): ReactNode {
  const t = useT();

  return (
    <Stack gap="md">
      <Title order={3}>
        {formatDate(report.periodStart)}–{formatDate(report.periodEnd)}
      </Title>

      {!report.isFinal && (
        /*
          The single most important sentence on this page. An open period's
          figures are summed from the journal every time they are asked for and
          will move as postings are made. Presenting them without this would let
          someone file a number that was true for a moment.
        */
        <Alert color="yellow" variant="light" title={t('vat.provisionalTitle')}>
          {t('vat.provisional')}
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Figure label={t('vat.outgoing')} value={report.outgoingVat} />
        <Figure label={t('vat.incoming')} value={report.incomingVat} />
        <Figure
          label={t('vat.netVat')}
          value={report.netVat}
          hint={t('vat.netVatHint')}
          strong
        />
      </SimpleGrid>

      {report.codesWithoutFixedRate.length > 0 && (
        <Alert color="blue" variant="light" title={t('vat.noFixedRateTitle')}>
          <Stack gap={4}>
            <Text size="sm">{report.codesWithoutFixedRate.join(', ')}</Text>
            <Text size="sm">{t('vat.noFixedRate')}</Text>
          </Stack>
        </Alert>
      )}

      {report.uncodedLineCount > 0 && (
        <Alert color="blue" variant="light" title={t('vat.uncodedTitle')}>
          <Text size="sm">
            {/* One key with two grammatical numbers, rather than the ternary
                that used to append an 'r' -- English pluralises the verb too. */}
            {t('vat.uncoded', { count: report.uncodedLineCount })}
          </Text>
        </Alert>
      )}

      {report.byCode.length > 0 && (
        <>
          <Text fw={500}>{t('vat.byCode')}</Text>
          <Table.ScrollContainer minWidth={700}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 90 }}>{t('vat.col.code')}</Table.Th>
                  <Table.Th>{t('vat.col.description')}</Table.Th>
                  <Table.Th style={{ width: 90 }}>{t('vat.col.rate')}</Table.Th>
                  <Table.Th style={{ width: 140 }} className="money-cell">
                    {t('vat.col.base')}
                  </Table.Th>
                  <Table.Th style={{ width: 140 }} className="money-cell">
                    {t('vat.title')}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {report.byCode.map((row) => (
                  <Table.Tr key={row.code}>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {row.code}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{row.label}</Text>
                      <Text size="xs" c="dimmed">
                        {row.vatGroup}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {/*
                        No rate and a zero rate are different statements, and
                        rendering the first as "0 %" would make the second up.
                      */}
                      {row.rate === null ? (
                        <Text size="sm" c="dimmed">
                          {row.rateNote ?? 'ingen fast sats'}
                        </Text>
                      ) : (
                        <Text size="sm">
                          {/*
                            Through Decimal, not Number. A rate is not money, but
                            it arrives as a decimal string for the same reason
                            money does, and multiplying it via a float is the
                            habit this codebase does not keep.
                          */}
                          {formatMoney(new Decimal(row.rate).times(100), 0)} %
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td className="money-cell">
                      {formatMoney(row.baseAmount)}
                    </Table.Td>
                    <Table.Td className="money-cell">
                      {formatMoney(row.vatAmount)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </>
      )}

      <ClosePeriod period={period} report={report} />
    </Stack>
  );
}

function Figure({
  label,
  value,
  hint,
  strong,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  readonly strong?: boolean;
}): ReactNode {
  return (
    <Card withBorder padding="md">
      <Stack gap={4}>
        <Text size="sm" c="dimmed">
          {label}
        </Text>
        <Text className="money-cell" fw={strong === true ? 700 : 600} size="lg">
          {formatMoney(value)}
        </Text>
        {hint !== undefined && (
          <Text size="xs" c="dimmed">
            {hint}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Filing
// -----------------------------------------------------------------------------

function ClosePeriod({
  period,
  report,
}: {
  readonly period: VatPeriod;
  readonly report: VatReport;
}): ReactNode {
  const t = useT();
  const { role } = useAuth();
  const close = useCloseVatPeriod();
  const [confirming, setConfirming] = useState(false);

  if (period.status === 'closed') {
    return (
      <Alert color="green" variant="light" title={t('vat.filedTitle')}>
        <Text size="sm">
          {period.closedAt === null
            ? t('vat.filed')
            : t('vat.filedOn', { date: formatDate(period.closedAt) })}
        </Text>
      </Alert>
    );
  }

  /*
    Only an owner may file, and a button that answers 403 is worse than no
    button: it invites the one action the person cannot take and explains
    nothing. So the affordance is replaced by the reason.
  */
  const isOwner = role === 'owner' || role === 'system_admin';
  if (!isOwner) {
    return (
      <Alert color="gray" variant="light" title={t('vat.ownerOnlyTitle')}>
        <Text size="sm">{t('vat.ownerOnly')}</Text>
      </Alert>
    );
  }

  if (!confirming) {
    return (
      <Group justify="flex-end">
        <Button
          color="orange"
          onClick={() => {
            setConfirming(true);
          }}
        >
          {t('vat.fileAndClose')}
        </Button>
      </Group>
    );
  }

  return (
    <Stack gap="sm">
      <Alert color="orange" variant="light" title={t('vat.irreversibleTitle')}>
        <Stack gap="xs">
          <Text size="sm">{t('vat.irreversible')}</Text>
          <Text size="sm" component="div">
            <ul style={{ margin: 0, paddingInlineStart: '1.25rem' }}>
              <li>
                {t('vat.irreversible.freeze', { amount: formatMoney(report.netVat) })}
              </li>
              <li>{t('vat.irreversible.noReopen')}</li>
              <li>{t('vat.irreversible.noPosting')}</li>
            </ul>
          </Text>
          <Text size="sm">{t('vat.irreversible.enforced')}</Text>
        </Stack>
      </Alert>

      {close.isError && (
        <ErrorMessage error={close.error} title={t('vat.closeFailed')} />
      )}

      <Group justify="flex-end">
        <Button
          variant="subtle"
          onClick={() => {
            setConfirming(false);
          }}
        >
          {t('vat.undo')}
        </Button>
        <Button
          color="orange"
          loading={close.isPending}
          onClick={() => {
            close.mutate(period.id, {
              onSuccess: () => {
                notifications.show({
                  color: 'green',
                  title: t('vat.closed'),
                  message: t('vat.closedDetail', {
                    amount: formatMoney(report.netVat),
                  }),
                });
                setConfirming(false);
              },
            });
          }}
        >
          {t('vat.fileAndCloseShort')}
        </Button>
      </Group>
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Opening a period
// -----------------------------------------------------------------------------

const openSchema = z.object({
  periodStart: z.date({ message: 'vat.startRequired' }),
  periodEnd: z.date({ message: 'vat.endRequired' }),
});

type OpenValues = z.infer<typeof openSchema>;

function OpenPeriodModal({
  opened,
  onClose,
}: {
  readonly opened: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const t = useT();
  const open = useOpenVatPeriod();

  const form = useForm<OpenValues>({
    mode: 'uncontrolled',
    initialValues: { periodStart: new Date(), periodEnd: new Date() },
    validate: translating(openSchema, t, 'vat'),
  });

  function handleSubmit(values: OpenValues): void {
    open.mutate(
      {
        periodStart: toIsoDate(values.periodStart),
        periodEnd: toIsoDate(values.periodEnd),
      },
      {
        onSuccess: (period) => {
          notifications.show({
            color: 'green',
            title: t('vat.opened'),
            message: `${formatDate(period.periodStart)}–${formatDate(period.periodEnd)}`,
          });
          onClose();
        },
      },
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('vat.openTitle')}>
      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('vat.openHelp')}
          </Text>

          <DateInput
            label={t('vat.startDate')}
            valueFormat="DD.MM.YYYY"
            key={form.key('periodStart')}
            {...form.getInputProps('periodStart')}
          />
          <DateInput
            label={t('vat.endDate')}
            valueFormat="DD.MM.YYYY"
            key={form.key('periodEnd')}
            {...form.getInputProps('periodEnd')}
          />

          {open.isError && (
            <ErrorMessage error={open.error} title={t('vat.openFailed')} />
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={open.isPending}>
              {t('vat.open')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
